import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GoogleLogin } from './GoogleLogin';
import { LoadingSpinner } from '../LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Forgot Password Modal Component
interface ForgotPasswordModalProps {
  onClose: () => void;
  initialEmail?: string;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ onClose, initialEmail = '' }) => {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset email');
      }

      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-dark-card rounded-2xl p-6 sm:p-8 max-w-[400px] w-[90%] relative shadow-dark-xl border border-dark-border"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 bg-none border-none text-2xl cursor-pointer text-dark-text-secondary hover:text-dark-text"
          onClick={onClose}
        >
          ×
        </button>

        {sent ? (
          <div className="text-center">
            <div className="w-[60px] h-[60px] rounded-full bg-gradient-to-br from-green-400 to-green-600 text-white text-[28px] flex items-center justify-center mx-auto mb-4">
              ✓
            </div>
            <h3 className="text-xl font-semibold text-dark-text mb-2">Check Your Email</h3>
            <p className="text-sm text-dark-text-secondary mb-5 leading-relaxed">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <button
              className="w-full py-3 px-6 bg-gradient-to-br from-amber-400 to-amber-500 text-dark-bg border-none rounded-lg text-base font-semibold cursor-pointer hover:opacity-90 transition-opacity"
              onClick={onClose}
            >
              Back to Login
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-semibold text-dark-text mb-2">Reset Password</h3>
            <p className="text-sm text-dark-text-secondary mb-5 leading-relaxed">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm mb-4">
                  {error}
                </div>
              )}

              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full py-3 px-4 border-2 border-dark-border rounded-lg text-base mb-4 box-border bg-dark-bg-tertiary text-dark-text outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                required
              />

              <button
                type="submit"
                disabled={loading || !email}
                className={`w-full py-3 px-6 bg-gradient-to-br from-amber-400 to-amber-500 text-dark-bg border-none rounded-lg text-base font-semibold cursor-pointer transition-opacity ${
                  loading || !email ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'
                }`}
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <LoadingSpinner size="small" label="Sending reset link" />
                    Sending...
                  </span>
                ) : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

interface LoginProps {
  onSwitchToRegister?: () => void;
  onSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSwitchToRegister, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { login, loading, error, clearError } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!email || !password) {
      return;
    }

    try {
      await login(email, password);
      if (onSuccess) onSuccess();
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleInputChange = () => {
    if (error) clearError();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-dark-bg via-slate-800 to-dark-bg p-4 sm:p-5 relative overflow-hidden">
      <div className="bg-slate-800/95 backdrop-blur-xl rounded-3xl shadow-dark-xl p-8 sm:p-12 w-full max-w-[460px] animate-fade-in border border-amber-400/20">
        <div className="text-center mb-8">
          <div className="mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-dark-bg text-4xl font-bold mx-auto shadow-[0_10px_30px_rgba(251,191,36,0.4),0_0_20px_rgba(251,191,36,0.2)]">
              O
            </div>
          </div>
          <h1 className="text-3xl sm:text-[32px] font-bold m-0 mb-2 bg-gradient-to-r from-white to-amber-400 bg-clip-text text-transparent">
            Welcome Back
          </h1>
          <p className="text-base text-slate-400 m-0">Sign in to your OBusinessCard account</p>
        </div>

        <GoogleLogin
          onSuccess={onSuccess}
          onError={(error) => {
            // Error is handled by the GoogleLogin component and auth context
          }}
        />

        <div className="flex items-center my-6">
          <span className="flex-1 h-px bg-slate-600/50"></span>
          <span className="px-4 text-sm text-slate-400">or continue with email</span>
          <span className="flex-1 h-px bg-slate-600/50"></span>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-center gap-2 text-red-300 text-sm">
              <span className="text-base">⚠️</span>
              {error}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-200">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                handleInputChange();
              }}
              placeholder="your@email.com"
              className="py-3.5 px-4 border-2 border-slate-600/50 rounded-xl text-base transition-all duration-200 outline-none bg-slate-900/50 text-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/10"
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-200">Password</label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  handleInputChange();
                }}
                placeholder="Enter your password"
                className="py-3.5 pl-4 pr-[50px] border-2 border-slate-600/50 rounded-xl text-base w-full transition-all duration-200 outline-none bg-slate-900/50 text-white focus:border-amber-400 focus:ring-2 focus:ring-amber-400/10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-none border-none cursor-pointer text-lg p-1 text-slate-400 hover:text-slate-200"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="text-right -mt-2">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="bg-none border-none text-amber-400 text-sm cursor-pointer underline p-0 hover:text-amber-300"
            >
              Forgot your password?
            </button>
          </div>

          {showForgotPassword && (
            <ForgotPasswordModal
              onClose={() => setShowForgotPassword(false)}
              initialEmail={email}
            />
          )}

          <button
            type="submit"
            disabled={loading || !email || !password}
            className={`w-full py-4 px-6 bg-gradient-to-br from-amber-400 to-amber-500 text-dark-bg border-none rounded-xl text-base font-bold cursor-pointer transition-all duration-200 shadow-[0_4px_15px_rgba(251,191,36,0.3)] ${
              loading || !email || !password
                ? 'opacity-60 cursor-not-allowed'
                : 'hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(251,191,36,0.4)]'
            }`}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <LoadingSpinner size="small" label="Signing in" />
                Signing In...
              </span>
            ) : 'Sign In'}
          </button>
        </form>

        <div className="text-center mt-8 pt-6 border-t border-slate-600/50">
          <p className="text-slate-400 text-sm m-0">
            Don't have an account?{' '}
            {onSwitchToRegister ? (
              <button
                onClick={onSwitchToRegister}
                className="bg-none border-none text-amber-400 font-semibold cursor-pointer p-0 text-sm underline hover:text-amber-300"
              >
                Create Account
              </button>
            ) : (
              <button
                onClick={onSwitchToRegister}
                className="bg-none border-none text-amber-400 cursor-pointer underline p-0 text-sm hover:text-amber-300"
              >
                Create Account
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
